if (!customElements.get('xyxx-transformation-videos')) {
  class XyxxTransformationVideos extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';
      this.track = this.querySelector('[data-video-track]');
      this.cards = [...this.querySelectorAll('[data-video-card]')];
      this.previousButton = this.querySelector('[data-video-prev]');
      this.nextButton = this.querySelector('[data-video-next]');
      this.progress = this.querySelector('[data-video-progress]');
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.abortController = new AbortController();
      this.scrollPauseFrame = null;

      if (!this.track || this.cards.length === 0) return;

      this.cards.forEach((card) => this.setupCard(card));
      this.setupObservers();
      this.setupNavigation();

      requestAnimationFrame(() => this.updateNavigation());
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.lazyObserver?.disconnect();
      this.autoplayObserver?.disconnect();
      this.resizeObserver?.disconnect();

      if (this.scrollPauseFrame) {
        cancelAnimationFrame(this.scrollPauseFrame);
      }

      this.dataset.initialized = 'false';
    }

    setupCard(card) {
      const video = card.querySelector('[data-video]');
      const playButton = card.querySelector('[data-video-toggle]');
      const soundButton = card.querySelector('[data-video-sound]');
      const mediaShell = card.querySelector('[data-video-shell]');

      if (!video || !playButton || !soundButton || !mediaShell) return;

      video.defaultMuted = true;
      video.muted = true;
      video.playsInline = true;
      video.loop = card.dataset.autoplay === 'true';

      const signal = this.abortController.signal;

      playButton.addEventListener(
        'click',
        (event) => {
          event.stopPropagation();
          this.toggleVideo(card, true);
        },
        { signal }
      );

      mediaShell.addEventListener(
        'click',
        (event) => {
          if (event.target.closest('[data-video-toggle], [data-video-sound]')) return;
          this.toggleVideo(card, true);
        },
        { signal }
      );

      soundButton.addEventListener(
        'click',
        (event) => {
          event.stopPropagation();

          if (video.muted) {
            this.muteOtherVideos(video);
            video.muted = false;
            video.volume = 1;
          } else {
            video.muted = true;
          }

          this.updateSoundButton(card);
        },
        { signal }
      );

      video.addEventListener(
        'play',
        () => {
          card.dataset.hasStarted = 'true';
          card.classList.add('is-playing');
          card.classList.remove('is-loading', 'has-error');
          playButton.setAttribute('aria-label', `Pause ${video.getAttribute('aria-label') || 'video'}`);
          this.updateSoundButton(card);
        },
        { signal }
      );

      video.addEventListener(
        'pause',
        () => {
          card.classList.remove('is-playing', 'is-loading');
          playButton.setAttribute('aria-label', `Play ${video.getAttribute('aria-label') || 'video'}`);
        },
        { signal }
      );

      video.addEventListener(
        'ended',
        () => {
          card.classList.remove('is-playing', 'is-loading');
          playButton.setAttribute('aria-label', `Replay ${video.getAttribute('aria-label') || 'video'}`);
        },
        { signal }
      );

      video.addEventListener(
        'loadeddata',
        () => card.classList.remove('is-loading'),
        { signal }
      );

      video.addEventListener(
        'error',
        () => {
          card.classList.remove('is-playing', 'is-loading');
          card.classList.add('has-error');
        },
        { signal }
      );

      this.updateSoundButton(card);
    }

    setupObservers() {
      if (!('IntersectionObserver' in window)) {
        this.cards.forEach((card) => {
          this.ensureVideoSources(card);

          if (card.dataset.autoplay === 'true' && !this.prefersReducedMotion) {
            this.playVideo(card, false);
          }
        });
        return;
      }

      this.lazyObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            this.ensureVideoSources(entry.target);
            this.lazyObserver.unobserve(entry.target);
          });
        },
        {
          rootMargin: '350px 0px',
          threshold: 0.01,
        }
      );

      this.autoplayObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const card = entry.target;
            const video = card.querySelector('[data-video]');

            if (!video || card.dataset.autoplay !== 'true') return;

            if (
              entry.isIntersecting &&
              entry.intersectionRatio >= 0.35 &&
              !this.prefersReducedMotion
            ) {
              this.playVideo(card, false);
            } else if (!entry.isIntersecting || entry.intersectionRatio < 0.2) {
              video.pause();
            }
          });
        },
        {
          threshold: [0, 0.2, 0.35, 0.65],
        }
      );

      this.cards.forEach((card) => {
        this.lazyObserver.observe(card);
        this.autoplayObserver.observe(card);
      });

      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.hidden) {
            this.cards.forEach((card) => card.querySelector('[data-video]')?.pause());
          }
        },
        { signal: this.abortController.signal }
      );
    }

    setupNavigation() {
      const signal = this.abortController.signal;

      this.previousButton?.addEventListener(
        'click',
        () => this.scrollByCard(-1),
        { signal }
      );

      this.nextButton?.addEventListener(
        'click',
        () => this.scrollByCard(1),
        { signal }
      );

      this.track.addEventListener(
        'scroll',
        () => {
          this.updateNavigation();

          if (this.scrollPauseFrame) {
            cancelAnimationFrame(this.scrollPauseFrame);
          }

          this.scrollPauseFrame = requestAnimationFrame(() => {
            this.pauseAllVideos();
          });
        },
        { passive: true, signal }
      );

      this.track.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.scrollByCard(-1);
          }

          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.scrollByCard(1);
          }
        },
        { signal }
      );

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateNavigation());
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', () => this.updateNavigation(), { signal });
      }
    }

    ensureVideoSources(card) {
      if (card.dataset.sourcesLoaded === 'true') return;

      const video = card.querySelector('[data-video]');
      const template = card.querySelector('[data-video-template]');

      if (!video || !template) return;

      video.appendChild(template.content.cloneNode(true));
      video.load();
      card.dataset.sourcesLoaded = 'true';
    }

    async playVideo(card, userInitiated) {
      const video = card.querySelector('[data-video]');
      if (!video) return;

      this.pauseOtherVideos(video);
      this.ensureVideoSources(card);
      card.classList.add('is-loading');

      if (!userInitiated || card.dataset.hasStarted !== 'true') {
        video.muted = true;
      }

      video.loop = card.dataset.autoplay === 'true';
      this.updateSoundButton(card);

      try {
        await video.play();
      } catch (error) {
        card.classList.remove('is-playing', 'is-loading');
      }
    }

    toggleVideo(card, userInitiated) {
      const video = card.querySelector('[data-video]');
      if (!video) return;

      if (video.paused || video.ended) {
        this.playVideo(card, userInitiated);
      } else {
        video.pause();
      }
    }

    pauseOtherVideos(activeVideo) {
      this.cards.forEach((card) => {
        const video = card.querySelector('[data-video]');
        if (!video || video === activeVideo) return;

        video.pause();
        video.muted = true;
        this.updateSoundButton(card);
      });
    }

    pauseAllVideos() {
      this.cards.forEach((card) => {
        const video = card.querySelector('[data-video]');
        if (!video || video.paused) return;

        video.pause();
      });
    }

    muteOtherVideos(activeVideo) {
      this.cards.forEach((card) => {
        const video = card.querySelector('[data-video]');
        if (!video || video === activeVideo) return;

        video.muted = true;
        this.updateSoundButton(card);
      });
    }

    updateSoundButton(card) {
      const video = card.querySelector('[data-video]');
      const soundButton = card.querySelector('[data-video-sound]');
      if (!video || !soundButton) return;

      const label = video.getAttribute('aria-label') || 'video';
      soundButton.classList.toggle('is-muted', video.muted);
      soundButton.setAttribute('aria-pressed', String(!video.muted));
      soundButton.setAttribute('aria-label', `${video.muted ? 'Unmute' : 'Mute'} ${label}`);
    }

    scrollByCard(direction) {
      const firstCard = this.cards[0];
      if (!firstCard) return;

      const trackStyles = window.getComputedStyle(this.track);
      const gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;
      const distance = firstCard.getBoundingClientRect().width + gap;

      this.track.scrollBy({
        left: direction * distance,
        behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
      });
    }

    updateNavigation() {
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const currentScroll = Math.min(maxScroll, Math.max(0, this.track.scrollLeft));
      const isStatic = maxScroll <= 2;

      this.classList.toggle('is-static', isStatic);

      if (this.previousButton) {
        this.previousButton.disabled = isStatic || currentScroll <= 2;
      }

      if (this.nextButton) {
        this.nextButton.disabled = isStatic || currentScroll >= maxScroll - 2;
      }

      if (!this.progress) return;

      if (isStatic) {
        this.progress.style.width = '100%';
        this.progress.style.left = '0%';
        return;
      }

      const visibleRatio = Math.min(1, this.track.clientWidth / this.track.scrollWidth);
      const scrollProgress = maxScroll > 0 ? currentScroll / maxScroll : 0;
      const progressWidth = Math.max(0.14, visibleRatio);
      const progressLeft = scrollProgress * (1 - progressWidth);

      this.progress.style.width = `${progressWidth * 100}%`;
      this.progress.style.left = `${progressLeft * 100}%`;
    }
  }

  customElements.define('xyxx-transformation-videos', XyxxTransformationVideos);
}
